// hooks/useCountryNudge.js
//
// A country code that has to be picked. When the visitor's country cannot be
// told (hooks/usePhoneCountry), the picker starts blank rather than on a
// guess - a wrong code sends the SMS nowhere, and someone asked to pick is
// someone whose code arrives. Submitting with it blank shakes the picker,
// rings it red and says why (CountryCodeHint).
//
//   const nudge = useCountryNudge(country);
//   if (!nudge.check()) return;            // in the submit handler
//   <button ref={nudge.ref} className={nudge.className}> ... </button>
//   <CountryCodeHint nudge={nudge} />

import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/CountryNudge.scss';

export const COUNTRY_CODE_HINT = 'Pick your country code';

const SHAKE = [0, -10, 10, -8, 8, -4, 0].map((x) => ({ transform: `translateX(${x}px)` }));

export default function useCountryNudge(country) {
  const ref = useRef(null);
  const [flagged, setFlagged] = useState(false);

  // Picked: nothing more to say.
  useEffect(() => { if (country) setFlagged(false); }, [country]);

  /** True when a country is picked; otherwise shakes the picker and returns false. */
  const check = useCallback(() => {
    if (country) return true;
    setFlagged(true);
    ref.current?.animate?.(SHAKE, { duration: 380, easing: 'ease-out' });
    navigator.vibrate?.(60);
    ref.current?.focus?.({ preventScroll: true });
    return false;
  }, [country]);

  return { ref, flagged, check, className: flagged ? 'country-missing' : '' };
}

/** "Pick your country code", under the number, while it is missing. */
export function CountryCodeHint({ nudge }) {
  if (!nudge?.flagged) return null;
  return <p className="country-code-hint" role="alert">{COUNTRY_CODE_HINT}</p>;
}
